import { useState } from "react";
import { Loader2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { field, card } from "@/components/admin/SettingsPanel";

const EMPTY = { title_en: "", title_mh: "", body_en: "", body_mh: "", link: "", active: true };

const Form = ({ initial, onDone }) => {
  const [f, setF] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] ?? EMPTY[k]]));
      const res = initial?.id ? await api.put(`/announcements/${initial.id}`, body) : await api.post("/announcements", body);
      if (res.data?.pending) toast("Sent for approval — it will appear once a Super Admin approves it");
      else toast.success("Announcement saved");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} data-testid="announcement-form" className={`${card} grid gap-3 sm:grid-cols-2`}>
      <input data-testid="ann-title-en" required placeholder="Title (English)" value={f.title_en} onChange={set("title_en")} className={field} />
      <input data-testid="ann-title-mh" placeholder="Title (Marshallese)" value={f.title_mh} onChange={set("title_mh")} className={field} />
      <textarea data-testid="ann-body-en" rows={3} placeholder="Message (English)" value={f.body_en} onChange={set("body_en")} className={field} />
      <textarea data-testid="ann-body-mh" rows={3} placeholder="Message (Marshallese)" value={f.body_mh} onChange={set("body_mh")} className={field} />
      <input data-testid="ann-link" placeholder="Link (optional) https://…" value={f.link} onChange={set("link")} className={field} />
      <label className="flex items-center gap-3 font-ui text-sm text-[#F4EEE2]">
        <Switch data-testid="ann-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} className="data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-[#3A4558]" /> Show on website
      </label>
      <div className="flex gap-3 sm:col-span-2">
        <button data-testid="ann-save-btn" disabled={busy} className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-2.5 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
        <button type="button" data-testid="ann-cancel-btn" onClick={onDone} className="rounded-full px-5 py-2.5 font-ui text-sm text-[#B7BFCC]">Cancel</button>
      </div>
    </form>
  );
};

export const AnnouncementsPanel = ({ isMaster }) => {
  const { announcements, refresh } = useSite();
  const [editing, setEditing] = useState(null);
  const done = () => { setEditing(null); refresh(); };
  const del = async (a) => {
    if (!window.confirm(`Delete "${a.title_en}"?`)) return;
    await api.delete(`/announcements/${a.id}`).then((r) => { if (r.data?.pending) toast("Delete sent for approval"); refresh(); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  };
  return (
    <div data-testid="announcements-panel" className="grid gap-4">
      {editing ? <Form initial={editing.id ? editing : null} onDone={done} /> : (
        <button data-testid="ann-add-btn" onClick={() => setEditing({})} className="flex w-fit items-center gap-2 rounded-full bg-[#F4EEE2] px-5 py-2.5 font-ui text-sm font-semibold text-[#0B111E] hover:bg-[#D4AF37]">
          <Plus className="h-4 w-4" /> New announcement
        </button>
      )}
      {!announcements.length && <p className="py-6 text-sm text-[#B7BFCC]">No announcements yet.</p>}
      {announcements.map((a) => (
        <div key={a.id} data-testid={`ann-row-${a.id}`} className="flex items-center gap-4 border-b border-[#F4EEE2]/10 py-4">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${a.active ? "bg-emerald-400" : "bg-[#4A5568]"}`} title={a.active ? "Visible" : "Hidden"} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-ui font-semibold text-[#F4EEE2]">{a.title_en}</p>
            <p className="truncate text-xs text-[#B7BFCC]">{a.active ? "Visible on site" : "Hidden"} · {a.body_en}</p>
          </div>
          <button data-testid={`ann-edit-${a.id}`} onClick={() => setEditing(a)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
          {isMaster && <button data-testid={`ann-delete-${a.id}`} onClick={() => del(a)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
        </div>
      ))}
    </div>
  );
};
